import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, PencilSimple, Trash, Wrench } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import VatRateSelect from '../components/VatRateSelect';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Services = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({
    name: '',
    category: '',
    description: '',
    unit_price: '',
    unit_label: 'unité',
    is_active: true,
    vat_rate: 0.085,
    subcontractor_price: '',
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/services`, { withCredentials: true });
      setServices(r.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', category: '', description: '', unit_price: '', unit_label: 'unité', is_active: true, vat_rate: 0.085, subcontractor_price: '' });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, unit_price: parseFloat(form.unit_price), subcontractor_price: parseFloat(form.subcontractor_price) || 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/services/${editing.id}`, payload, { withCredentials: true });
        toast.success('Intervention mise à jour');
      } else {
        await axios.post(`${API_URL}/api/services`, payload, { withCredentials: true });
        toast.success('Intervention créée');
      }
      setShowDialog(false);
      resetForm();
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      category: s.category || '',
      description: s.description || '',
      unit_price: String(s.unit_price),
      unit_label: s.unit_label || 'unité',
      is_active: s.is_active !== false,
      vat_rate: s.vat_rate !== undefined ? s.vat_rate : 0.085,
      subcontractor_price: s.subcontractor_price !== undefined ? String(s.subcontractor_price) : '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Supprimer cette intervention ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/services/${id}`, { withCredentials: true });
      toast.success('Intervention supprimée');
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const filtered = services.filter(s =>
    !searchQuery ||
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.category || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="services-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Interventions à domicile</h1>
            <p className="text-slate-600">Gérez votre catalogue d'interventions à domicile</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowDialog(true); }}
            data-testid="create-service-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Nouvelle intervention
          </Button>
        </div>

        <div className="mb-4">
          <Input
            placeholder="Rechercher une intervention..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-services-input"
            className="w-full md:w-96"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Catégorie</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Description</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix unitaire</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Unité</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                      <div className="flex items-center gap-2">
                        <Wrench size={16} className="text-blue-600" />
                        {s.name}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{s.category || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{s.description || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-900 text-right font-medium">{s.unit_price.toFixed(2)} €</td>
                    <td className="py-3 px-4 text-sm text-slate-600">/{s.unit_label}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                        s.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {s.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleEdit(s)}
                        data-testid={`edit-service-${s.id}`}
                        className="inline-flex p-2 text-orange-600 hover:bg-orange-50 rounded-md mr-1"
                      >
                        <PencilSimple size={18} />
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={() => handleDelete(s.id)}
                          data-testid={`delete-service-${s.id}`}
                          className="inline-flex p-2 text-red-700 hover:bg-red-50 rounded-md"
                        >
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="7" className="py-12 text-center text-slate-500">Aucune intervention</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
          <DialogContent data-testid="service-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier l\'intervention' : 'Nouvelle intervention'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="service-name-input"
                  required
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="service-category-input"
                    placeholder="Impression, Reliure, Service..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Unité</label>
                  <Input
                    value={form.unit_label}
                    onChange={(e) => setForm({ ...form, unit_label: e.target.value })}
                    data-testid="service-unit-input"
                    placeholder="page, carte, heure..."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="service-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prix unitaire TTC (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  data-testid="service-price-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Prix sous-traitant TTC (€) <span className="text-xs text-slate-500">— montant à régler au technicien (TTC)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.subcontractor_price}
                  onChange={(e) => setForm({ ...form, subcontractor_price: e.target.value })}
                  data-testid="service-subcontractor-price-input"
                  placeholder="0.00"
                />
                {form.subcontractor_price && !isNaN(parseFloat(form.subcontractor_price)) && (
                  <p className="text-xs text-slate-500 mt-1">
                    Montant HT que facturera le sous-traitant : <strong>{(parseFloat(form.subcontractor_price) / (1 + (parseFloat(form.vat_rate) || 0.085))).toFixed(2)} €</strong> (TVA {((parseFloat(form.vat_rate) || 0.085) * 100).toFixed(1)} %)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taux de TVA</label>
                <VatRateSelect
                  value={form.vat_rate}
                  onChange={(v) => setForm({ ...form, vat_rate: v })}
                  testId="service-vat-rate-select"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="service-active-input"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">Intervention active</label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-service-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Services;
